import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {DisabledText} from 'src/components/profile-tab-structure';
import {Skeleton} from 'src/components/skeleton';
import {ACCOUNT_TABS} from 'src/constants';
import {searchAccountProductSubscriptions} from 'src/openapi/admin/apis/AccountProductSubscriptionsApi';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';

import {ProductAccessSummarySectionSkeleton} from './product-access-summary-section-skeleton';
import {SubscriptionRow} from './subscription-row';

import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';

const MAX_SUBSCRIPTIONS = 5;

type Props = {
    accountId: number,
};

export const ProductAccessSummarySection: React.FC<Props> = ({accountId}) => {
    const apiDispatch = useAppDispatch();
    const [subscriptions, setSubscriptions] = useState<ProductSubscription[] | null>(null);
    const navigate = useNavigate();
    const [count, setCount] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const abortController = new AbortController();
        const baseRequest = {
            pagination: {page: 1, pageSize: MAX_SUBSCRIPTIONS},
        };

        Promise.all([
            apiDispatch(searchAccountProductSubscriptions({
                accountId,
                productSubscriptionSearchRequest: {
                    ...baseRequest,
                    filters: {lifecycleStatuses: [ProductSubscriptionLifecycleStatus.ACTIVE]},
                },
            }, {signal: abortController.signal})),
            apiDispatch(searchAccountProductSubscriptions({
                accountId,
                productSubscriptionSearchRequest: {
                    ...baseRequest,
                    filters: {lifecycleStatuses: [ProductSubscriptionLifecycleStatus.PENDING]},
                },
            }, {signal: abortController.signal})),
        ]).then(([activeRes, pendingRes]) => {
            if (cancelled) {
                return;
            }

            const active = activeRes.payload?.success ? activeRes.payload.data?.content ?? [] : [];
            const pending = pendingRes.payload?.success ? pendingRes.payload.data?.content ?? [] : [];
            const totalCount = (activeRes.payload?.success ? activeRes.payload.data?.totalCount || 0 : 0)
                + (pendingRes.payload?.success ? pendingRes.payload.data?.totalCount || 0 : 0);
            setCount(totalCount);
            setSubscriptions([...active, ...pending].slice(0, MAX_SUBSCRIPTIONS));
        });

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [accountId, apiDispatch]);

    const onManageClick = useCallback(() => {
        navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.PRODUCT_ACCESS.name}`);
    }, [navigate, accountId]);

    const title = subscriptions && subscriptions.length > 0
        ? `Product Access Summary: ${subscriptions && subscriptions.length} of ${count}`
        : 'Product Access Summary';

    return (
        <ErrorBoundary>
            <Skeleton view={<ProductAccessSummarySectionSkeleton/>} shown={subscriptions === null}>
                <FormSection
                    headline={title}
                    info={headline => (
                        <Item grow={1}>
                            <Group justify="space-between" alignItems="center">
                                {headline}
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={onManageClick}>
                                    Manage
                                </Button>
                            </Group>
                        </Item>
                    )}>
                    <Group direction="v" alignItems="start" gap="xs">
                        {subscriptions && subscriptions.length > 0
                            ? subscriptions.map(subscription => (
                                    <SubscriptionRow
                                        key={subscription.productSubscriptionId}
                                        subscription={subscription}
                                    />
                                ))
                            : null}
                        {subscriptions && subscriptions.length === 0 ? (
                            <DisabledText>This account has no active product subscriptions</DisabledText>
                        ) : null}
                    </Group>
                </FormSection>
            </Skeleton>
        </ErrorBoundary>
    );
};
